/**
 * Animation tokens for spring presets, durations, and scale values.
 * These provide consistent motion across the app. Spring configs are
 * designed for react-native-reanimated's `withSpring`.
 *
 * No Figma source — motion tokens are code-defined since Figma has
 * no animation/motion variables in the current design system.
 */

/** Spring preset configs for `withSpring`. */
export const spring = {
  /** Quick press-feedback bounce-back. */
  snappy: { damping: 10, stiffness: 260, mass: 0.4 },
  /** Gentle positional slide (e.g. highlight indicator). */
  smooth: { damping: 18, stiffness: 220, mass: 0.6 },
  /** Lively scale pop (e.g. active-tab emphasis). */
  bouncy: { damping: 12, stiffness: 240, mass: 0.4 },
} as const;

/** Spring preset configs for the RN `Animated.spring` API (`tension`/`friction`). */
export const springRn = {
  /** Scale-in entrance for the `bounce` motion variant. */
  bouncy: { tension: 200, friction: 8 },
} as const;

/** Named durations (ms) for `withTiming`. */
export const duration = {
  /** One measured loading shimmer sweep. */
  shimmer: 1100,
  /** Instant press squish. */
  press: 90,
  /** Short transition (tab switch emphasis). */
  transition: 140,
  /** Fade in/out for opacity changes. */
  fade: 160,
  /**
   * Dissolve between two states of the same element (e.g. a status indicator
   * swapping spinner for checkmark, or a card's copy swapping under it). Longer
   * than `fade` on purpose — a same-element swap reads as a change of state
   * rather than an arrival, and a short dissolve reads as a pop.
   */
  crossfade: 240,
  /** Expand/collapse reveal (callouts, animated reveal panels). */
  reveal: 220,
  /** Slightly faster exit for animated reveal panels. */
  revealExit: 200,
  /** Full-screen step transition — matches the native stack push/pop feel. */
  screenTransition: 300,
  /** One full revolution of a continuous indeterminate spinner. */
  spin: 800,
} as const;

/** Cubic-bezier control points for named timing curves. */
export const easing = {
  /** Scale entrance with an overshoot before settling, for web bounce motion. */
  bouncy: [0.34, 1.56, 0.64, 1] as const,
  /**
   * Balanced ease-in-out for deliberate positional transitions. Code-defined
   * while the current Figma library has no motion variables (see file header).
   */
  standard: [0.42, 0, 0.58, 1] as const,
} as const;

/** Preset scale multipliers for press and active states. */
export const scale = {
  /** Scale-down on press. */
  pressed: 0.86,
  /** Scale-up for active emphasis. */
  active: 1.08,
  /** Starting scale for an element that scales in as it appears. */
  entry: 0.6,
  /**
   * Starting scale for a dissolve-in that should read as a settle rather than a
   * pop — paired with `duration.crossfade`.
   */
  gentleEntry: 0.9,
} as const;

/** Entrance-motion defaults for the server-authored `Motion` wrapper. */
export const motion = {
  /** Default per-item stagger interval (ms) applied when a spec omits `staggerMs`. */
  staggerMs: 72,
  /** Vertical offset (px) an element rises from for the `fade_slide_up` intent. */
  translateY: 10,
} as const;

/**
 * Opacity pulse presets for a live-state indicator (e.g. a presence dot).
 * A pulse cycles between full opacity and `opacityFloor`, reversing every
 * `halfCycleMs` with an ease-in-out character.
 */
export const pulse = {
  /** Slow, deep pulse for waiting states (connecting, pending, idle-listening). */
  waiting: { opacityFloor: 0.35, halfCycleMs: 600 },
  /** Faster, shallower pulse conveying active output (e.g. audio playing). */
  active: { opacityFloor: 0.6, halfCycleMs: 360 },
} as const;

/**
 * Scale kick played when a streamed item lands (e.g. a revealed word):
 * rises to `scale` with an ease-out character, settles back with ease-in-out.
 * Asymmetric rise/settle keeps a rapid cadence from reading as jitter.
 */
export const kick = { scale: 1.1, riseMs: 140, settleMs: 320 } as const;

/** Fades for a live content block leaving focus. */
export const fadeOut = {
  /** Finished content dims but stays readable (ease-out). */
  rest: { opacity: 0.5, durationMs: 1000 },
  /** Cancelled content clears entirely (e.g. an interrupted reply). */
  dismiss: { opacity: 0, durationMs: 250 },
} as const;

/**
 * Pending-content bounce for a multi-dot loading indicator: each dot rises
 * `translateY` over `durationMs` (ease-out) and falls back over the same
 * duration (ease-in), with dots offset by `staggerMs` each.
 */
export const bounce = { translateY: -5, durationMs: 400, staggerMs: 200 } as const;

/**
 * Expanding halo ripple for input-activity feedback (e.g. voice detected):
 * scales to `scale` over `durationMs` (ease-out) while opacity rises to
 * `peakOpacity` over `fadeInMs` then decays to 0 over `durationMs` (ease-in).
 */
export const ripple = {
  scale: 2.5,
  durationMs: 1000,
  peakOpacity: 0.6,
  fadeInMs: 200,
  /** Halos rendered at once; further triggers are dropped until one retires. */
  maxConcurrent: 3,
  /** Cadence of follow-up halos while the triggering activity continues. */
  repeatMs: 5000,
} as const;

/** Streamed-text reveal pacing. */
export const reveal = {
  /** A revealed word's hidden-to-visible fade (ease-in), starting as it is spoken. */
  wordFadeMs: 150,
  /** Cadence used when no provider timing exists: reveal advances per character. */
  fallbackMsPerChar: 60,
} as const;

export type Spring = typeof spring;
export type Duration = typeof duration;
export type EasingTokens = typeof easing;
export type Scale = typeof scale;
export type MotionTokens = typeof motion;
export type Pulse = typeof pulse;
export type Kick = typeof kick;
export type FadeOut = typeof fadeOut;
export type Bounce = typeof bounce;
export type Ripple = typeof ripple;
export type Reveal = typeof reveal;

/** Code-defined loading highlight angle; not a Figma dimension. */
export const skeletonMotion = { tiltDegrees: 30 } as const;
